// Tiny i18n layer: React Context + `useI18n()` hook.
//
// Detection
// ---------
// • Initial language: stored choice → navigator.languages → fallback "en".
// • Emergency number: if any browser language tag or the user agent contains
//   "CH" (e.g. "de-CH", "fr-CH"), we use 144 (Swiss emergency); otherwise
//   112. The detection is locale-based and independent of the chosen UI
//   language — Swiss users keep getting 144 even when they switch the UI
//   language to French or Italian.
//
// Translation
// -----------
// `t(key, vars?)` returns the localised string. Two placeholders are
// always substituted automatically: `{num}` (emergency number) and any
// `{notruf}` token used by the disclaimer/hint sentences.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    DEFAULT_LANGUAGE,
    LANGUAGES,
    SUPPORTED_CODES,
    translations,
} from "./translations";

const STORAGE_KEY = "notfall.lang";

const detectInitialLang = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && SUPPORTED_CODES.includes(stored)) return stored;
    } catch {
        /* noop */
    }
    if (typeof navigator !== "undefined") {
        const candidates = [
            ...(navigator.languages || []),
            navigator.language || "",
        ];
        for (const c of candidates) {
            const code = (c || "").toLowerCase().split("-")[0];
            if (SUPPORTED_CODES.includes(code)) return code;
        }
    }
    return DEFAULT_LANGUAGE;
};

// Country hint detection.
//
// Per stakeholder request the app **always** dials 112 (universally
// valid across the EU and beyond). To reassure users in countries where
// the local emergency number is more widely known, we surface a small
// hint under the button:
//   - Switzerland → "In der Schweiz auch als 144 bekannt – 112 funktioniert genauso"
//   - United Kingdom → "In the UK also known as 999 – 112 works just the same"
// Detection is browser-locale based (no IP look-up).
const detectCountryHint = () => {
    if (typeof navigator === "undefined") return "";
    const tags = [];
    if (navigator.language) tags.push(navigator.language);
    if (Array.isArray(navigator.languages)) tags.push(...navigator.languages);
    for (const tag of tags) {
        if (typeof tag !== "string") continue;
        if (/(^|[-_])ch($|[-_])/i.test(tag)) return "ch";
        if (/(^|[-_])gb($|[-_])/i.test(tag)) return "uk";
        if (/(^|[-_])uk($|[-_])/i.test(tag)) return "uk";
    }
    return "";
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
    const [lang, setLangState] = useState(() => detectInitialLang());

    // Always dial 112 – the emergency number does not change with the
    // active UI language.
    const emergencyNumber = "112";
    // Region hint frozen at first mount so it doesn't flicker on language
    // switches.
    const emergencyHint = useMemo(() => detectCountryHint(), []);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch {
            /* noop */
        }
        if (typeof document !== "undefined") {
            document.documentElement.lang = lang;
        }
    }, [lang]);

    const setLang = useCallback((next) => {
        if (SUPPORTED_CODES.includes(next)) setLangState(next);
    }, []);

    const t = useCallback(
        (key, vars) => {
            const entry = translations[key];
            if (!entry) return key;
            let value = entry[lang] || entry[DEFAULT_LANGUAGE] || entry.de || key;
            // Always-on substitution for the emergency number; user vars win.
            const merged = { num: emergencyNumber, ...(vars || {}) };
            value = value.replace(/\{(\w+)\}/g, (_, k) =>
                merged[k] !== undefined ? String(merged[k]) : `{${k}}`
            );
            return value;
        },
        [lang, emergencyNumber]
    );

    const value = useMemo(
        () => ({
            lang,
            setLang,
            t,
            languages: LANGUAGES,
            emergencyNumber,
            emergencyHint,
        }),
        [lang, setLang, t, emergencyNumber, emergencyHint]
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
    const ctx = useContext(I18nContext);
    if (!ctx) {
        throw new Error("useI18n must be used inside <I18nProvider>");
    }
    return ctx;
};

export const getCurrentLangSync = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && SUPPORTED_CODES.includes(stored)) return stored;
    } catch {
        /* noop */
    }
    return detectInitialLang();
};
